import { useCallback, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UploadZone } from "@/components/generate/upload-zone";
import { AuthModal } from "@/components/ui/auth-modal";
import { NsfwOrError } from "@/components/ui/nsfw-error";
import { useGenerate } from "@/hooks/use-generation";
import { api } from "@/lib/api";
import { useUserStore } from "@/stores/user-store";
import { INTEGRATED_MODEL_ID, POWER_TIER_LABEL } from "@/config/models";
import { ArrowRight, Gift, Loader2, Sparkles, Volume2 } from "lucide-react";

const PROMO_ID = "free_h3_15s";
const ANIMATE_HREF = "/animate-character";

interface PromoStatus {
  success: boolean;
  active: boolean;
  eligible: boolean;
  used: boolean;
  has_purchased?: boolean;
  reason: string | null;
  model_id: string;
  duration_seconds: number;
}

export function useFreeH3PromoStatus(userId?: string | null) {
  return useQuery({
    queryKey: ["promo", "free-h3-15s", userId ?? "anon"],
    queryFn: async () => {
      const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : "";
      return api.get<PromoStatus>(`/promo/free-h3-15s${qs}`);
    },
    staleTime: 30_000,
  });
}

export function FreeGenPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isSignedIn } = useUser();
  const { hasPurchased } = useUserStore();
  const { data: promo, isLoading } = useFreeH3PromoStatus(user?.id);
  const generateMutation = useGenerate();

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [showAuth, setShowAuth] = useState(false);

  const canGenerate = !!imageUrl && prompt.trim().length > 0;

  const handleGenerate = useCallback(async () => {
    if (!imageUrl || !prompt.trim()) return;
    try {
      const result = await generateMutation.mutateAsync({
        model_id: INTEGRATED_MODEL_ID,
        prompt: prompt.trim(),
        reference_image: imageUrl,
        duration_seconds: 15,
        promo: PROMO_ID,
      });
      await queryClient.invalidateQueries({ queryKey: ["promo", "free-h3-15s"] });
      if (result.video?.id) navigate(`/generation/${result.video.id}`);
    } catch (err) {
      console.error("Free generation failed:", err);
    }
  }, [imageUrl, prompt, generateMutation, navigate, queryClient]);

  if (isLoading) {
    return (
      <div className="max-w-lg mx-auto px-5 py-16 flex justify-center">
        <Loader2 className="size-6 animate-spin text-[#d4a574]" />
      </div>
    );
  }

  if (!promo?.active || promo.used || (isSignedIn && hasPurchased && !promo.eligible && promo.reason === "already_used")) {
    return <Navigate to="/" replace />;
  }

  if (isSignedIn && !hasPurchased) {
    return (
      <div className="max-w-lg mx-auto px-5 py-12 text-center space-y-4">
        <Gift className="size-10 text-[#d4a574] mx-auto" />
        <h1 className="text-2xl font-extrabold text-foreground">Purchasers only</h1>
        <p className="text-[14px] text-muted-foreground leading-relaxed">
          This limited-time free 15s {POWER_TIER_LABEL} generation is for anyone who&apos;s bought a gizmo pack.
        </p>
        <Link to="/billing" className="btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-xl text-[14px]">
          <Sparkles className="size-4" />
          Get Gizmos
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-5 py-8 space-y-6">
      <div className="rounded-2xl border border-[#d4a574]/30 bg-gradient-to-br from-[#d4a574]/15 to-white/5 p-5 space-y-2">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#d4a574] text-[#1a1520] text-[10px] font-bold uppercase tracking-wider">
          <Gift className="size-3" />
          Limited time
        </div>
        <h1 className="text-2xl font-extrabold text-foreground tracking-tight">
          Free Generation — 15 Second
        </h1>
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          One free {POWER_TIER_LABEL} video with native audio for purchasers. Upload an image, write a prompt, and go — 0 gizmos.
        </p>
        <p className="text-[12px] text-foreground/80 font-medium flex items-center gap-1.5 pt-1">
          <Volume2 className="size-3.5 text-[#d4a574]" />
          Locked to {POWER_TIER_LABEL} · 15s · with sound
        </p>
      </div>

      <UploadZone imageUrl={imageUrl} onImageChange={setImageUrl} />

      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Character turns and smiles, hair flowing in the breeze, cherry blossoms falling..."
        rows={3}
        className="input-field resize-none"
      />

      <Show when="signed-out">
        <button
          type="button"
          onClick={() => setShowAuth(true)}
          className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2 rounded-xl"
        >
          <Sparkles className="size-4" />
          Sign Up to Claim
        </button>
        <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} title="Sign Up to Claim Free Gen" />
      </Show>

      <Show when="signed-in">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate || generateMutation.isPending}
          className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2 rounded-xl"
        >
          {generateMutation.isPending ? (
            <><Loader2 className="size-4 animate-spin" />Generating free 15s...</>
          ) : (
            <><Gift className="size-4" />Generate Free 15s Video</>
          )}
        </button>
      </Show>

      {generateMutation.isError && <NsfwOrError message={generateMutation.error?.message} />}

      <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-center space-y-2">
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          Want more {POWER_TIER_LABEL} 15s videos? Generate them anytime on Animate (uses gizmos).
        </p>
        <Link to={ANIMATE_HREF} className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#d4a574] hover:underline">
          Go to Animate
          <ArrowRight className="size-3.5" />
        </Link>
      </div>
    </div>
  );
}
